import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  useTheme,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WindowsIcon from "@mui/icons-material/Window";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { apiServer, api } from "../api";
import { useLogin } from "../contexts/LoginContext";

interface IReleaseInfo {
  versao: string;
  sha256: string;
  obrigatorio: boolean;
  caminho: string;
}

interface IReleasesManifest {
  windows?: IReleaseInfo | null;
  linux?: IReleaseInfo | null;
}

export default function GerenciadorAtualizacoes() {
  const theme = useTheme();
  const [login] = useLogin();
  const [releases, setReleases] = useState<IReleasesManifest>({});
  const [loading, setLoading] = useState(true);
  const [copiadoSha, setCopiadoSha] = useState<string | null>(null);

  // Modal de Publicação
  const [modalAberto, setModalAberto] = useState(false);
  const [platform, setPlatform] = useState<"windows" | "linux">("windows");
  const [versao, setVersao] = useState("");
  const [obrigatorio, setObrigatorio] = useState(true);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [progressoLeitura, setProgressoLeitura] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ tipo: "success" | "error"; msg: string } | null>(null);

  const carregarReleases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiServer<IReleasesManifest>().get("v2/admin/updates", login.accessToken);
      setReleases(res.data || {});
    } catch (err) {
      console.error("Erro ao carregar releases:", err);
    } finally {
      setLoading(false);
    }
  }, [login.accessToken]);

  useEffect(() => {
    carregarReleases();
  }, [carregarReleases]);

  const handleCopiarSha = (sha: string) => {
    navigator.clipboard.writeText(sha);
    setCopiadoSha(sha);
    setTimeout(() => setCopiadoSha(null), 2000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setArquivoSelecionado(file);
      const match = file.name.match(/(\d+\.\d+\.\d+)/);
      if (match && !versao) {
        setVersao(match[1]);
      }
    }
  };

  const handlePublicar = async () => {
    if (!arquivoSelecionado || !versao.trim()) {
      setFeedback({ tipo: "error", msg: "Preencha a versão e selecione o arquivo do pacote (.zip ou .tar.gz)." });
      return;
    }

    setPublicando(true);
    setProgressoLeitura(10);

    const reader = new FileReader();

    reader.onprogress = (evt) => {
      if (evt.lengthComputable) {
        const percent = Math.round((evt.loaded / evt.total) * 50);
        setProgressoLeitura(percent);
      }
    };

    reader.onload = async () => {
      try {
        setProgressoLeitura(60);
        const base64Data = (reader.result as string).split(",")[1];
        setProgressoLeitura(80);

        const payload = {
          platform,
          versao: versao.trim(),
          obrigatorio,
          nomeArquivo: arquivoSelecionado.name,
          conteudoBase64: base64Data
        };

        const res = await api.post<{ sucesso: boolean; mensagem: string; release: IReleaseInfo }>(
          "v2/admin/updates/upload",
          payload,
          {
            headers: {
              Authorization: "Basic " + login.accessToken
            }
          }
        );

        setProgressoLeitura(100);
        setFeedback({ tipo: "success", msg: res.data.mensagem || "Versão publicada com sucesso!" });
        setModalAberto(false);
        setArquivoSelecionado(null);
        setVersao("");
        carregarReleases();
      } catch (err: any) {
        console.error("Erro ao publicar pacote:", err);
        setFeedback({
          tipo: "error",
          msg: err?.response?.data?.mensagem || "Erro ao realizar o upload da nova versão."
        });
      } finally {
        setPublicando(false);
        setProgressoLeitura(null);
      }
    };

    reader.onerror = () => {
      setFeedback({ tipo: "error", msg: "Falha ao ler o arquivo localmente." });
      setPublicando(false);
      setProgressoLeitura(null);
    };

    reader.readAsDataURL(arquivoSelecionado);
  };

  const renderCardPlataforma = (nomePlataforma: "windows" | "linux", titulo: string, release: IReleaseInfo | null | undefined) => (
    <Card
      sx={{
        borderRadius: "16px",
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: theme.palette.mode === "dark" ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#ffffff"
      }}
    >
      <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <WindowsIcon sx={{ color: nomePlataforma === "windows" ? "#0078D4" : "#E95420", fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                {titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {nomePlataforma === "windows" ? "Executáveis e bibliotecas Win64" : "Binários para distribuições Linux"}
              </Typography>
            </Box>
          </Box>
          {release ? (
            <Chip
              label={`v${release.versao}`}
              color="primary"
              sx={{ fontWeight: 700, fontSize: "0.9rem" }}
            />
          ) : (
            <Chip label="Sem versão" color="default" size="small" />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {release ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                STATUS DE ATUALIZAÇÃO
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={release.obrigatorio ? "Atualização Obrigatória" : "Atualização Opcional"}
                  color={release.obrigatorio ? "warning" : "default"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                ARQUIVO / PACOTE EM PRODUÇÃO
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600, mt: 0.5 }}>
                {release.caminho}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                HASH SHA-256
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    p: 0.8,
                    borderRadius: "6px",
                    wordBreak: "break-all",
                    flexGrow: 1,
                    fontSize: "0.75rem"
                  }}
                >
                  {release.sha256}
                </Typography>
                <Tooltip title={copiadoSha === release.sha256 ? "Copiado!" : "Copiar Hash SHA-256"}>
                  <IconButton size="small" onClick={() => handleCopiarSha(release.sha256)}>
                    {copiadoSha === release.sha256 ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ mt: "auto", pt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<DownloadIcon />}
                onClick={() => window.open(`/api/v2/updates/download/${nomePlataforma}/${release.versao}`, "_blank")}
                sx={{ borderRadius: "8px", textTransform: "none" }}
              >
                Testar Download do Pacote (v{release.versao})
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma versão configurada para esta plataforma.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
            Gerenciamento de Atualizações do Agente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Publique novas versões do agente cliente para que os computadores em produção se atualizem de forma automática.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={carregarReleases}
            disabled={loading}
            sx={{ borderRadius: "8px" }}
          >
            Recarregar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setModalAberto(true)}
            sx={{ borderRadius: "8px", fontWeight: 600 }}
          >
            Publicar Nova Versão
          </Button>
        </Box>
      </Box>

      {/* Feedback Alert */}
      {feedback && (
        <Alert
          severity={feedback.tipo}
          onClose={() => setFeedback(null)}
          sx={{ mb: 3, borderRadius: "10px" }}
        >
          {feedback.msg}
        </Alert>
      )}

      {/* Grid de Plataformas Ativas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {renderCardPlataforma("windows", "Agente Windows (Win64)", releases.windows)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderCardPlataforma("linux", "Agente Linux (x86_64)", releases.linux)}
        </Grid>
      </Grid>

      {/* Guia de Ajuda e Boas Práticas */}
      <Accordion defaultExpanded sx={{ borderRadius: "16px !important", border: `1px solid ${theme.palette.divider}`, boxShadow: "none", mb: 4 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HelpOutlineIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Guia Passo a Passo: Como Compilar e Publicar uma Nova Versão
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}>
                1. Compilação dos Binários no Delphi
              </Typography>
              <List dense>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Compile todos os projetos em modo Release Win64 no Delphi 12 / 12.3."
                    secondary="Certifique-se de que os arquivos BackupFacil.Core.dll, BackupFacil.Client.exe, BackupFacil.Launcher.exe e BackupFacil.Updater.exe estejam atualizados na pasta Win64/Release."
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Execute o script de empacotamento ou compacte a pasta Release."
                    secondary="Você pode usar o script 'package-releases.ps1' na raiz do projeto ou compactar manualmente."
                  />
                </ListItem>
              </List>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1, color: "primary.main" }}>
                2. Estrutura Obrigatória do Arquivo ZIP
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                backupfacil-agent-windows-1.2.6.zip<br />
                ├── BackupFacil.Core.dll<br />
                ├── BackupFacil.Client.exe<br />
                ├── BackupFacil.Daemon.exe<br />
                ├── BackupFacil.Launcher.exe<br />
                ├── BackupFacil.Updater.exe<br />
                └── 7z/ (ou 7z.dll)
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}>
                3. Publicação e Geração de Hash Automática
              </Typography>
              <List dense>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Clique no botão 'Publicar Nova Versão' acima."
                    secondary="Informe a nova versão (ex: 1.2.6), selecione o arquivo .zip e clique em Publicar."
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText
                    primary="O servidor calcula o Hash SHA-256 automaticamente."
                    secondary="O arquivo é salvo em updates/releases/ e o manifesto releases.json é atualizado instantaneamente."
                  />
                </ListItem>
              </List>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1, color: "primary.main" }}>
                4. Como o Agente Cliente se Atualiza
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                O serviço no cliente consulta periodicamente o endpoint <code>/api/v2/updates/check</code>. Ao detectar que existe uma versão superior:
              </Typography>
              <List dense>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="1. Baixa o pacote em segundo plano sem interromper backups em andamento." />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="2. Valida a integridade do arquivo através do Hash SHA-256 fornecido pela API." />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="3. Aciona o BackupFacil.Updater.exe que substitui os binários e reinicia o serviço com segurança." />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Modal de Publicação de Versão */}
      <Dialog
        open={modalAberto}
        onClose={() => !publicando && setModalAberto(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <CloudUploadIcon color="primary" />
          Publicar Nova Versão do Agente
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Plataforma de Destino</InputLabel>
              <Select
                value={platform}
                label="Plataforma de Destino"
                onChange={(e) => setPlatform(e.target.value as any)}
                disabled={publicando}
              >
                <MenuItem value="windows">Windows (Win64 - .zip)</MenuItem>
                <MenuItem value="linux">Linux (x86_64 - .tar.gz)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Número da Versão (Semântica)"
              placeholder="Ex: 1.2.6"
              value={versao}
              onChange={(e) => setVersao(e.target.value)}
              fullWidth
              size="small"
              disabled={publicando}
              helperText="Utilize o formato Maior.Menor.Correcao (ex: 1.2.6)"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={obrigatorio}
                  onChange={(e) => setObrigatorio(e.target.checked)}
                  color="primary"
                  disabled={publicando}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Atualização Obrigatória
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Força os clientes com versões inferiores a atualizarem no próximo ciclo.
                  </Typography>
                </Box>
              }
            />

            <Box
              sx={{
                border: `2px dashed ${arquivoSelecionado ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: "12px",
                p: 3,
                textAlign: "center",
                backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                cursor: publicando ? "default" : "pointer",
                "&:hover": { borderColor: theme.palette.primary.main }
              }}
              onClick={() => !publicando && document.getElementById("input-upload-release")?.click()}
            >
              <input
                id="input-upload-release"
                type="file"
                accept={platform === "windows" ? ".zip" : ".tar.gz,.zip"}
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={publicando}
              />
              <CloudUploadIcon sx={{ fontSize: 40, color: arquivoSelecionado ? "primary.main" : "text.secondary", mb: 1 }} />
              {arquivoSelecionado ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {arquivoSelecionado.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(arquivoSelecionado.size / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Clique para selecionar o arquivo compactado ({platform === "windows" ? ".zip" : ".tar.gz"})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ou arraste e solte o arquivo aqui
                  </Typography>
                </Box>
              )}
            </Box>

            {publicando && (
              <Box sx={{ width: "100%" }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Enviando pacote e calculando SHA-256 no servidor...
                </Typography>
                <LinearProgress variant={progressoLeitura ? "determinate" : "indeterminate"} value={progressoLeitura || 0} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setModalAberto(false)}
            disabled={publicando}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handlePublicar}
            disabled={publicando || !arquivoSelecionado || !versao.trim()}
            variant="contained"
            color="primary"
            startIcon={publicando ? <CircularProgress size={18} color="inherit" /> : <SystemUpdateAltIcon />}
            sx={{ borderRadius: "8px", fontWeight: 600 }}
          >
            {publicando ? "Publicando..." : "Confirmar e Publicar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
